import { ChangeDetectionStrategy, Component, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../core/service/auth.service';
import { BrandService } from '../../../shared/services/brand.service';
import { IconComponent } from '../../../shared/icon/icon.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, TranslateModule, IconComponent],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
  @Output() toggleSidenav = new EventEmitter<void>();

  profileName = '';
  profileInitials = '';
  showUserMenu = false;
  selectedBrand: 'robuschi' | 'pedrogil' = 'robuschi';

  readonly brands = [
    { code: 'robuschi' as const, label: 'Robuschi', logo: 'assets/images/robuschi-logo.png' },
    { code: 'pedrogil' as const, label: 'Pedro Gil', logo: 'assets/images/pedro-gil-logo.png' },
  ];

  get currentLogo(): string {
    return this.brands.find((b) => b.code === this.selectedBrand)?.logo ?? this.brands[0].logo;
  }

  readonly availableLanguages = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
    { code: 'fr', label: 'Français' },
    { code: 'it', label: 'Italiano' },
    { code: 'de', label: 'Deutsch' },
  ];

  private readonly pedroGilLangs = new Set(['en', 'es']);

  get filteredLanguages() {
    return this.selectedBrand === 'pedrogil'
      ? this.availableLanguages.filter((l) => this.pedroGilLangs.has(l.code))
      : this.availableLanguages;
  }

  selectedLanguage = 'en';

  constructor(
    private readonly auth: AuthService,
    private readonly translate: TranslateService,
    private readonly brandService: BrandService,
  ) {
    translate.addLangs(this.availableLanguages.map((l) => l.code));
    translate.setDefaultLang('en');
  }

  ngOnInit(): void {
    this.selectedLanguage = localStorage.getItem('language') || 'en';
    this.translate.use(this.selectedLanguage);
    const savedBrand = this.brandService.current;
    this.selectedBrand = savedBrand;
    this.updateFavicon(this.selectedBrand);
    this.applyBrandColor(this.selectedBrand);
    this.enforceLangForBrand(this.selectedBrand);

    const user = this.auth.getUser();
    if (user) {
      this.profileName = user.fullName || user.username || '';
      const parts = this.profileName
        .trim()
        .split(/\s+/)
        .filter((p) => p.length > 0);
      this.profileInitials =
        parts.length >= 2
          ? (parts[0][0] + (parts.at(-1) ?? '')[0]).toUpperCase()
          : (this.profileName.trim().slice(0, 2) || 'U').toUpperCase();
    }
  }

  onToggleSidenav(): void {
    this.toggleSidenav.emit();
  }

  changeLanguage(code: string): void {
    this.selectedLanguage = code;
    localStorage.setItem('language', code);
    this.translate.use(code);
  }

  changeBrand(code: 'robuschi' | 'pedrogil'): void {
    this.selectedBrand = code;
    this.brandService.set(code); // notifies all subscribers via BehaviorSubject
    this.updateFavicon(code);
    this.applyBrandColor(code);
    this.enforceLangForBrand(code);
  }

  private enforceLangForBrand(brand: 'robuschi' | 'pedrogil'): void {
    if (brand === 'pedrogil' && !this.pedroGilLangs.has(this.selectedLanguage)) {
      this.changeLanguage('en');
    }
  }

  private applyBrandColor(brand: 'robuschi' | 'pedrogil'): void {
    const isPedroGil = brand === 'pedrogil';
    document.documentElement.style.setProperty('--brand-color', isPedroGil ? '#041D86' : '#1f2a5b');
    document.documentElement.style.setProperty('--brand-rgb', isPedroGil ? '4, 29, 134' : '31, 42, 91');
  }

  private updateFavicon(brand: 'robuschi' | 'pedrogil'): void {
    const logo = this.brands.find((b) => b.code === brand)?.logo ?? this.brands[0].logo;
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (link) {
      link.href = logo;
    }
  }

  trackByLangCode(_index: number, lang: { code: string }): string {
    return lang.code;
  }

  trackByBrandCode(_index: number, brand: { code: string }): string {
    return brand.code;
  }

  toggleUserMenu(): void {
    this.showUserMenu = !this.showUserMenu;
  }

  logout(): void {
    this.showUserMenu = false;
    this.auth.logout();
  }
}
